// Zadanie otwarte E8: zadanie tekstowe na rownanie.
// Troje przyjaciol zbiera plakaty; z zaleznosci ukladamy rownanie i liczymy,
// ile ma kazde z nich.
//
// Parametry:
//   b   - liczba plakatow Basi (niewiadoma),  b = k * unit
//   d   - o ile wiecej ma Andrzej:            Andrzej = b + d
//   k   - ile razy mniej ma Marek:            Marek   = b / k = unit
//   m   - ile razy wiecej maja razem A + M:   Andrzej + Marek = m * b
// Tozsamosc (zawsze spelniona): (b + d) + b/k = m * b.

import React from "react";
import { BlockMath, InlineMath } from "../../../components/math/KatexWrappers";
import EquationInput from "../../../components/inputs/EquationInput";
import FractionInput from "../../../components/inputs/FractionInput";
import { scoreStaged } from "../../openTaskScoring";

const ORIGINAL_NAMES = { more: "Andrzej", base: "Basia", baseGen: "Basi", fewer: "Marek" };
const NAME_SETS = [
  ORIGINAL_NAMES,
  { more: "Ola", base: "Kasia", baseGen: "Kasi", fewer: "Tomek" },
  { more: "Adam", base: "Ewa", baseGen: "Ewy", fewer: "Kuba" },
  { more: "Maja", base: "Zosia", baseGen: "Zosi", fewer: "Janek" },
  { more: "Piotr", base: "Ania", baseGen: "Ani", fewer: "Wojtek" },
];

function pick(array) {
  return array[Math.floor(Math.random() * array.length)];
}

function buildData({ unit, k, m, names = ORIGINAL_NAMES }) {
  const base = k * unit; // Basia
  const d = unit * (k * (m - 1) - 1); // o ile wiecej ma Andrzej
  const more = base + d; // Andrzej
  const fewer = unit; // Marek = base / k

  return {
    taskType: "E8PostersWordProblemOpenTask",
    names,
    item: "plakaty",
    itemGen: "plakatów",
    unit,
    k,
    m,
    d,
    base, // Basia
    more, // Andrzej
    fewer, // Marek
    maxPoints: 3,
  };
}

function createOriginalTask() {
  // CKE: Andrzej o 28 wiecej od Basi, Marek 3 razy mniej, A+M = 2 razy wiecej.
  // => Basia 42, Andrzej 70, Marek 14.
  return {
    type: "E8PostersWordProblemOpenTask",
    data: buildData({ unit: 14, k: 3, m: 2, names: ORIGINAL_NAMES }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 17,
    },
    tags: ["e8", "algebra", "rownania", "zadanie-tekstowe", "zadanie-otwarte"],
  };
}

function createRandomTask() {
  // Dobieramy parametry tak, aby wszystkie liczby byly calkowite i dodatnie,
  // a wynik nie byl zbyt duzy.
  let data;
  do {
    const k = pick([2, 3, 4]);
    const m = pick([2, 3]);
    const unit = pick([3, 4, 5, 6, 7, 8, 9, 10, 12]);
    data = buildData({ unit, k, m, names: pick(NAME_SETS) });
  } while (data.d <= 0 || data.more > 300 || data.d === 0);

  return {
    type: "E8PostersWordProblemOpenTask",
    data,
    source: { variant: "random", basedOn: "E8_2025_MAJ_17" },
    tags: ["e8", "algebra", "rownania", "zadanie-tekstowe", "zadanie-otwarte"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return createRandomTask();
}

function parseInteger(raw) {
  if (raw == null) return null;
  const compact = String(raw).trim().replace(/\s+/g, "");
  if (!/^-?\d+$/.test(compact)) return null;
  return Number(compact);
}

function rawValue(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value.raw === "string") return value.raw;
  return "";
}

function normalizeText(raw) {
  return String(raw || "")
    .toLowerCase()
    .replace(/[ąćęłńóśźż]/g, (char) =>
      ({ ą: "a", ć: "c", ę: "e", ł: "l", ń: "n", ó: "o", ś: "s", ź: "z", ż: "z" }[char] || char)
    )
    .replace(/[−–—]/g, "-")
    .replace(/\\cdot|·|\*/g, "")
    .replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, "$1/$2")
    .replace(/,/g, ".")
    .replace(/\s+/g, "");
}

function hasAny(text, patterns) {
  return patterns.some((pattern) => text.includes(pattern));
}

function relationExpression(raw) {
  const text = normalizeText(raw);
  if (!text) return "";
  const parts = text.split("=");
  return parts[parts.length - 1] || "";
}

function isX(expr) {
  return expr === "x";
}

function coefficientOfX(expr) {
  if (expr === "x") return 1;
  const match = expr.match(/^(-?\d+(?:\.\d+)?)x$/);
  if (!match) return null;
  return Number(match[1]);
}

function isApprox(value, target, tolerance = 0.01) {
  return Number.isFinite(value) && Math.abs(value - target) <= tolerance;
}

function isXPlus(expr, d) {
  return hasAny(expr, [`x+${d}`, `${d}+x`]);
}

function isXMinus(expr, d) {
  return expr.includes(`x-${d}`);
}

function isXDiv(expr, k) {
  return hasAny(expr, [`x/${k}`, `x:${k}`, `xĂ·${k}`]);
}

function isDecimalXDiv(expr, k) {
  return isApprox(coefficientOfX(expr), 1 / k);
}

function textHasXDiv(text, k) {
  if (hasAny(text, [`x/${k}`, `x:${k}`])) return true;
  const coeffMatches = text.match(/-?\d+(?:\.\d+)?x/g) || [];
  return coeffMatches.some((part) => isDecimalXDiv(part, k));
}

function isParenXMinusDiv(expr, d, k) {
  return hasAny(expr, [`(x-${d})/${k}`, `(x-${d}):${k}`, `x-${d}/${k}`]);
}

function isKX(expr, k) {
  return hasAny(expr, [`${k}x`, `${k}(x)`, `x${k}`]);
}

function isKXPlus(expr, k, d) {
  return hasAny(expr, [`${k}x+${d}`, `${k}(x)+${d}`, `${d}+${k}x`, `${d}+${k}(x)`]);
}

function relationFieldsLookCorrect(userAnswer = {}, data) {
  const { d, k } = data;
  const more = relationExpression(userAnswer.relationMore);
  const base = relationExpression(userAnswer.relationBase);
  const fewer = relationExpression(userAnswer.relationFewer);
  if (!more && !base && !fewer) return false;

  const baseAsX = isX(base) && isXPlus(more, d) && (isXDiv(fewer, k) || isDecimalXDiv(fewer, k));
  const moreAsX = isX(more) && isXMinus(base, d) && isParenXMinusDiv(fewer, d, k);
  const fewerAsX = isX(fewer) && isKX(base, k) && isKXPlus(more, k, d);

  return baseAsX || moreAsX || fewerAsX;
}

function relationTextLooksCorrect(raw, data) {
  const text = normalizeText(raw);
  if (!text || !text.includes("x")) return false;
  const { d, k } = data;

  const baseAsX =
    text.includes(`x+${d}`) &&
    hasAny(text, [`x/${k}`, `x:${k}`, `x÷${k}`]);

  const moreAsX =
    text.includes(`x-${d}`) &&
    hasAny(text, [`(x-${d})/${k}`, `(x-${d}):${k}`, `x-${d}/${k}`]);

  const fewerAsX =
    hasAny(text, [`${k}x`, `${k}(x)`]) &&
    hasAny(text, [`${k}x+${d}`, `${k}(x)+${d}`]);

  return (
    baseAsX ||
    (text.includes(`x+${d}`) && textHasXDiv(text, k)) ||
    moreAsX ||
    fewerAsX
  );
}

function relationLooksCorrect(userAnswer, data) {
  return relationFieldsLookCorrect(userAnswer, data) || relationTextLooksCorrect(userAnswer?.relations, data);
}

function equationLooksCorrect(raw, data) {
  const text = normalizeText(raw);
  if (!text || !text.includes("=") || !text.includes("x")) return false;
  const { d, k, m } = data;

  const baseAsX =
    text.includes(`x+${d}`) &&
    hasAny(text, [`x/${k}`, `x:${k}`, `x÷${k}`]) &&
    hasAny(text, [`${m}x`, `${m}(x)`]);

  const moreAsX =
    text.includes(`x-${d}`) &&
    hasAny(text, [`(x-${d})/${k}`, `(x-${d}):${k}`, `x-${d}/${k}`]) &&
    hasAny(text, [`${m}(x-${d})`, `${m}x-${m * d}`]);

  const fewerAsX =
    hasAny(text, [`${k}x+${d}`, `${k}(x)+${d}`]) &&
    hasAny(text, [`${m * k}x`, `${m}(${k}x)`]);

  return (
    baseAsX ||
    (text.includes(`x+${d}`) && textHasXDiv(text, k) && hasAny(text, [`${m}x`, `${m}(x)`])) ||
    moreAsX ||
    fewerAsX
  );
}

export function renderTask({ data }) {
  const { names, itemGen, d, k, m } = data;
  const baseName = names.baseGen || names.base;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Troje przyjaciół – {names.more}, {names.base} i {names.fewer} – zbiera{" "}
        {data.item}. {names.more} ma o <InlineMath math={`${d}`} /> {itemGen}{" "}
        więcej od {baseName}, a {names.fewer}{" "}
        ma ich <InlineMath math={`${k}`} /> razy mniej od{" "}
        {baseName}. {names.more} i{" "}
        {names.fewer} mają razem <InlineMath math={`${m}`} /> razy więcej{" "}
        {itemGen} od {baseName}.
      </p>
      <p>
        Oblicz, ile {itemGen} ma każde z tych przyjaciół. Zapisz obliczenia.
      </p>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const setField = (field) => (value) => {
    onChange({ ...(userAnswer || {}), [field]: value });
  };

  const relationRow = (name, field, placeholder) => (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ fontWeight: 600 }}>{name} =</div>
      <EquationInput
        value={userAnswer[field] || ""}
        onChange={setField(field)}
        disabled={disabled}
        placeholder={placeholder}
        showPreview={true}
        basis="260px"
      />
    </div>
  );

  const equationRow = () => (
    <div style={{ display: "grid", gap: 6 }}>
      <EquationInput
        value={userAnswer.equation || ""}
        onChange={setField("equation")}
        disabled={disabled}
        placeholder="np. (x+3)+x/2=2x"
        showPreview={true}
        basis="420px"
      />
    </div>
  );

  const textRow = (label, field) => {
    if (field === "relations") {
      return (
        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ fontWeight: 700 }}>{label}</div>
          {relationRow(data.names.more, "relationMore", "np. x+3")}
          {relationRow(data.names.base, "relationBase", "np. x")}
          {relationRow(data.names.fewer, "relationFewer", "np. x/2")}
        </div>
      );
    }
    if (field === "equation") {
      return (
        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ fontWeight: 700 }}>{label}</div>
          {equationRow()}
        </div>
      );
    }
    return null;
  };

  const row = (label, field, placeholder) => (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ fontWeight: 600 }}>{label}:</div>
      <FractionInput
        label={label}
        value={userAnswer[field]}
        onChange={setField(field)}
        disabled={disabled}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left" }}>
      {textRow(
        "Krok 1. Zapisz zależności między liczbami plakatów.",
        "relations"
      )}
      {textRow(
        "Krok 2. Zapisz równanie wynikające z treści.",
        "equation"
      )}
      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ fontWeight: 700 }}>Krok 3. Podaj liczby plakatów.</div>
        {row(data.names.more, "more", "np. 12")}
        {row(data.names.base, "base", "np. 8")}
        {row(data.names.fewer, "fewer", "np. 4")}
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const relationsOk = relationLooksCorrect(userAnswer, data);
  const equationOk = equationLooksCorrect(userAnswer.equation, data);
  const more = parseInteger(rawValue(userAnswer.more));
  const base = parseInteger(rawValue(userAnswer.base));
  const fewer = parseInteger(rawValue(userAnswer.fewer));

  const moreOk = more === data.more;
  const baseOk = base === data.base;
  const fewerOk = fewer === data.fewer;
  const valuesOk = moreOk && baseOk && fewerOk;

  return scoreStaged({
    resultOk: valuesOk,
    milestones: [relationsOk, equationOk],
    maxScore: data.maxPoints,
    parts: {
      "zależności": relationsOk,
      "równanie": equationOk,
      "wyniki": valuesOk,
    },
  });
}

export function renderAnswer({ data }) {
  const { names, d, k, m, base, more, fewer } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>{"Przyk\u0142adowa poprawna odpowied\u017a:"}</div>
      <BlockMath math={`${names.more}:\\ x+${d},\\qquad ${names.base}:\\ x,\\qquad ${names.fewer}:\\ \\dfrac{x}{${k}}`} />
      <BlockMath math={`(x+${d})+\\dfrac{x}{${k}}=${m}x`} />
      <BlockMath math={`x=${base}`} />
      <BlockMath math={`${names.more}=${base}+${d}=${more},\\qquad ${names.fewer}=\\dfrac{${base}}{${k}}=${fewer}`} />
      <div>{"Odpowied\u017a: "}{names.base}{" ma "}<InlineMath math={`${base}`} />{"; "}{names.more}{" ma "}<InlineMath math={`${more}`} />{"; "}{names.fewer}{" ma "}<InlineMath math={`${fewer}`} />{"."}</div>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}, task) {
  const names = task?.data?.names || ORIGINAL_NAMES;
  const fmt = (v) => {
    const n = parseInteger(rawValue(v));
    return n == null ? <i>(brak)</i> : <InlineMath math={`${n}`} />;
  };
  const textOrEmpty = (value) => value || <i>(brak)</i>;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        Zaleznosci: {names.more} = {textOrEmpty(userAnswer.relationMore)},{" "}
        {names.base} = {textOrEmpty(userAnswer.relationBase)},{" "}
        {names.fewer} = {textOrEmpty(userAnswer.relationFewer)}
      </div>
      <div>Rownanie: {textOrEmpty(userAnswer.equation)}</div>
      <div>
        {names.more}: {fmt(userAnswer.more)}, {names.base}: {fmt(userAnswer.base)},{" "}
        {names.fewer}: {fmt(userAnswer.fewer)}
      </div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const { names, d, k, m, base, more, fewer } = data;
  const baseName = names.baseGen || names.base;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Oznaczmy liczbę {data.itemGen}{" "}
        {baseName} przez <InlineMath math="x" />. Zapisujemy pozostałe liczby:
      </p>
      <BlockMath
        math={`${names.more}:\\ x+${d}, \\qquad ${names.fewer}:\\ \\dfrac{x}{${k}}`}
      />
      <p>
        <strong>Krok 2.</strong> {names.more} i {names.fewer} mają razem{" "}
        <InlineMath math={`${m}`} /> razy więcej niż {baseName}, więc układamy
        równanie:
      </p>
      <BlockMath math={`\\left(x+${d}\\right)+\\dfrac{x}{${k}}=${m}x`} />
      <p>
        <strong>Krok 3.</strong> Rozwiązujemy równanie.
      </p>
      <BlockMath math={`${d}+\\dfrac{x}{${k}}=${m}x-x`} />
      <BlockMath math={`${d}=${m - 1}x-\\dfrac{x}{${k}}=\\dfrac{${(m - 1) * k - 1}x}{${k}}`} />
      <BlockMath math={`x=\\dfrac{${d}\\cdot ${k}}{${(m - 1) * k - 1}}=${base}`} />
      <p>
        <strong>Krok 4.</strong> Obliczamy pozostałe liczby.
      </p>
      <BlockMath
        math={`${names.more}=${base}+${d}=${more},\\qquad ${names.fewer}=\\dfrac{${base}}{${k}}=${fewer}`}
      />
      <p>
        {names.base} ma <InlineMath math={`${base}`} />, {names.more} ma{" "}
        <InlineMath math={`${more}`} />, a {names.fewer} ma{" "}
        <InlineMath math={`${fewer}`} /> {data.itemGen}.
      </p>
    </div>
  );
}
